/**
 * Faithful line-break rendering & editing for node-form text fields.
 * See docs/plans/todo/nodeform-text-linebreak-fidelity.md.
 *
 * A hardcoded allow-set of (modelName, fieldName) pairs gets "faithful" text
 * treatment — the same three things for every entry:
 *   1. READ:  render VT (U+000B, the tofu box) / CR / CRLF as real line breaks
 *             (toDisplayText normalizes → LF for display only).
 *   2. WRITE: render a controlled <textarea> so breaks are authorable; the
 *             textarea's natural output is LF, stored canonically on edit.
 *   3. GATE:  NodeForm drops an untouched faithful key from the save payload so
 *             an incidental save never rewrites the raw bytes of a row the user
 *             didn't touch (dirty-gate — the load-bearing safety net).
 *
 * The allow-set is EVIDENCE-DRIVEN: every pair below was confirmed (2026-07-17)
 * to carry VT/CR in the live DB across content_* columns and their PLD copies.
 * Keyed by the PAIR, never the bare field name (`content-onboarding.text` is
 * deliberately excluded — measured out, plan §2b). Do NOT blanket-convert.
 *
 * Two flavours, captured by `panel`:
 *   - panel:true  → a hard-no-wrap 3D display panel. The VR renderer does NOT
 *                   auto-wrap, so we warn (hintKey) and set wrap="off" (horizontal
 *                   scroll mimics the panel overflow).
 *   - panel:false → an avatar-AI LLM prompt (fed to the model, not a panel): no
 *                   line-length constraint, soft-wrap, no misleading hint.
 */
export type FaithfulTextConfig = {
  panel: boolean;
  hintKey: string | null;
};

const PANEL_HINT = "pages.project.editNode.panelTextNoWrapHint";
const TEXT_PANEL_HINT = "pages.project.editNode.multilineTextNoWrapHint";

const panel = (hintKey: string = PANEL_HINT): FaithfulTextConfig => ({ panel: true, hintKey });
const prompt = (): FaithfulTextConfig => ({ panel: false, hintKey: null });

const FAITHFUL_TEXT_FIELDS: Record<string, FaithfulTextConfig> = {
  "content-text::text": panel(TEXT_PANEL_HINT),
  "content-quizz-3::question_text": panel(),
  "content-quizz-image-multiple::question_text": panel(),
  "content-zone-detect::entitled": panel(),
  "content-zone-detect::instruction": panel(),
  "content-zone-detect::title": panel(),
  "content-zone-sort::entitled": panel(),
  "content-element-catch::text_instructions": panel(),
  "content-danger-catch::text_success": panel(),
  "content-danger-catch::text_fail": panel(),
  "content-photo-catch::text_success": panel(),
  "content-photo-catch::text_fail": panel(),
  "content-hotspot-scene-image::text": panel(),
  "content-hotspot-scene-video::text": panel(),
  "content-quizz-3::explanation_true_text": panel(),
  "content-quizz-3::explanation_false_text": panel(),
  "content-quizz-image-multiple::explanation_true_text": panel(),
  "content-quizz-image-multiple::explanation_false_text": panel(),
  "content-yes-no-question::explanation_true_text": panel(),
  "content-yes-no-question::explanation_false_text": panel(),
  "content-speech-to-check::fail_text": panel(),
  "content-fire-extinguisher::instructions_panel_text": panel(),
  "content-avatar-ai-persona::avatar_role": prompt(),
  "content-avatar-ai-persona::user_role": prompt(),
  "content-avatar-ai-persona::context_prompt": prompt(),
  "content-avatar-ai-persona::user_success_prompt": prompt(),
  "content-avatar-ai-persona::user_failure_prompt": prompt(),
};

export const getFaithfulTextConfig = (
  modelName: string | null | undefined,
  fieldName: string,
): FaithfulTextConfig | null =>
  (modelName && FAITHFUL_TEXT_FIELDS[`${modelName}::${fieldName}`]) || null;

export const isFaithfulTextField = (
  modelName: string | null | undefined,
  fieldName: string,
): boolean => !!getFaithfulTextConfig(modelName, fieldName);

/**
 * Read-normalizer (display only). The corpus stores three live line-break
 * conventions (plan §2, confirmed by a full-DB scan 2026-07-17): VT (U+000B),
 * CR / CRLF (U+000D), and LF (U+000A) — and NOTHING else (zero LS/PS/other-C0).
 * Map them all to a canonical LF so a control char renders as a real break in
 * the textarea. Every other char is left untouched. CRLF is collapsed before VT
 * so `\r\n` yields one break, not two.
 */
export const toDisplayText = (value: unknown): string => {
  if (typeof value === "string") return value.replace(/\r\n?/g, "\n").replace(/\v/g, "\n");
  if (value == null) return "";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};
